/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/*
  Class Description

  This class is responsible for rendering a list item in the list of Alternative Satellites in FOV. Mousing into and out
  of the item dispatches actions to the Redux Store to set and unset the hovered alternative satellite in FOV which is
  needed in the Look Angles Graphic and on the map
 */
import React from 'react';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { DataPath } from '../../../../shared/entities/terminal';
import {
  arrayIndexToLetter,
  EMPTY_FIELD_STRING_PLACEHOLDER,
  transformRLWaveformName,
} from '../../../../utils/StringUtils';
import { getSatelliteStatusColor } from '../../../../utils/TerminalUtilities';
import StyledProps from '../../../common/theme/AppThemeInterface';
import TruncateWithTooltip from '../../../common/TruncateWithTooltip';
import { SatelliteDisplayProps } from '../TabbedPanel/TerminalTabPanel';
import { SteerableBeamsRing } from '../../../../shared/entities/steerableBeam';
import HcxShortestDistanceToBeamEdgeDisplay from '../../HcxShortestDistanceToBeamEdgeDisplay';

const HUB_TOOLTIP_WIDTH_PX = 226;
const SATELLITE_NAME_TOOLTIP_WIDTH_PX = 252;

export interface SatelliteInFovListItemProps extends SatelliteDisplayProps {
  hideLink: boolean;
  hideStats: boolean;
  beamName: string;
  steerableBeamsRingList: SteerableBeamsRing[];
}

function SatelliteInFovListItem({
  hideLink,
  hideStats,
  satelliteInFov,
  beamName,
  steerableBeamsRingList,
  index,
  isHighlighted,
  isStickyMode,
  setHighlightedSatelliteInFov,
}: SatelliteInFovListItemProps) {
  const handleMouseEnter = () => {
    if (!isStickyMode) {
      setHighlightedSatelliteInFov({
        satelliteName: satelliteInFov.satelliteName,
        sticky: false,
        setByGraphicIndicator: false,
        setBySatelliteList: true,
      });
    }
  };
  const handleSatelliteClick = (event: MouseEvent) => {
    event.stopPropagation();
    setHighlightedSatelliteInFov({
      satelliteName: satelliteInFov.satelliteName,
      sticky: true,
      setByGraphicIndicator: false,
      setBySatelliteList: true,
    });
  };
  const beam = steerableBeamsRingList.find((beam: SteerableBeamsRing) => beam.beamName === beamName);
  const shortestDistanceToBeamEdge =
    !isNil(beam) && !isNil(beam.terminalShortestDistanceToBeamEdgeMapping)
      ? beam.terminalShortestDistanceToBeamEdgeMapping[satelliteInFov.datapathUuid]
      : null;

  return (
    <StyledSatelliteInFovListItem
      hideStats={hideStats}
      hasHcxDistanceToEdge={!isNil(shortestDistanceToBeamEdge)}
      onClick={handleSatelliteClick}
      onMouseEnter={handleMouseEnter}
    >
      <div
        data-test="satellite_fov_list_item__data_wrapper"
        className={`data-wrapper ${!isHighlighted ? 'faded' : ''}`}
      >
        <div className="header">
          <div className="circle" style={{ backgroundColor: getSatelliteStatusColor(satelliteInFov) }}>
            {arrayIndexToLetter(index)}
          </div>
          {satelliteInFov.isAlternative ? (
            <span data-test="satellite_fov_list_item__alternative">Alternative</span>
          ) : (
            <span data-test="satellite_fov_list_item__current">Current</span>
          )}
          <span>&nbsp;</span>
          {satelliteInFov.dataPath === DataPath.KU ? (
            <span data-test="satellite_fov_list_item__ku_beam">Ku Beam</span>
          ) : satelliteInFov.dataPath === DataPath.KA ? (
            <span data-test="satellite_fov_list_item__ka_beam">Ka Beam</span>
          ) : satelliteInFov.dataPath === DataPath.GX ? (
            <span data-test="satellite_fov_list_item__gx_beam">Gx Beam</span>
          ) : (
            `${EMPTY_FIELD_STRING_PLACEHOLDER} Beam`
          )}
          {':'}
          <div data-test="satellite_fov_list_item__satellite" className="satellite">
            <TruncateWithTooltip
              text={
                !isNil(satelliteInFov) && Boolean(satelliteInFov.satelliteName)
                  ? satelliteInFov.satelliteName
                  : EMPTY_FIELD_STRING_PLACEHOLDER
              }
              tooltipMaxWidthPx={SATELLITE_NAME_TOOLTIP_WIDTH_PX}
            />
          </div>
          {!satelliteInFov.isAlternative && satelliteInFov.connectivityStatus && (
            <span
              data-test="satellite_fov_list_item__connectivity"
              className="connectivity"
              style={{ color: getSatelliteStatusColor(satelliteInFov) }}
            >
              {satelliteInFov.connectivityStatus}
            </span>
          )}
        </div>
        {!isNil(shortestDistanceToBeamEdge) && (
          <HcxShortestDistanceToBeamEdgeDisplay shortestDistanceToBeamEdge={shortestDistanceToBeamEdge} />
        )}
        {!hideStats && (
          <React.Fragment>
            <div className="background__line" />
            <div className="stat-wrapper">
              <div className="azimuth label">Azimuth</div>
              <div className="azimuth value" data-test="satellite_fov_list_item__azimuth">
                {!isNil(satelliteInFov.azimuthDeg)
                  ? `${satelliteInFov.azimuthDeg.toFixed(2)}°`
                  : EMPTY_FIELD_STRING_PLACEHOLDER}
              </div>
              <div className="elevation label">Elevation</div>
              <div className="elevation value" data-test="satellite_fov_list_item__elevation">
                {satelliteInFov && !isNil(satelliteInFov.elevationDeg)
                  ? `${satelliteInFov.elevationDeg.toFixed(2)}°`
                  : EMPTY_FIELD_STRING_PLACEHOLDER}
              </div>
              <div className="hub label">Hub</div>
              <div className="hub value" data-test="satellite_fov_list_item__hub">
                <TruncateWithTooltip
                  text={
                    satelliteInFov && !isNil(satelliteInFov.hubName) && satelliteInFov.hubName.length
                      ? satelliteInFov.hubName
                      : EMPTY_FIELD_STRING_PLACEHOLDER
                  }
                  tooltipMaxWidthPx={HUB_TOOLTIP_WIDTH_PX}
                />
              </div>
              {!hideLink && satelliteInFov.dataPath === DataPath.KU && (
                <React.Fragment>
                  <div className="link label">Link</div>
                  <div className="link value" data-test="satellite_fov_list_item__link">
                    <TruncateWithTooltip
                      text={
                        satelliteInFov && !isNil(satelliteInFov.link) && satelliteInFov.link.length
                          ? transformRLWaveformName(satelliteInFov.link)
                          : EMPTY_FIELD_STRING_PLACEHOLDER
                      }
                      tooltipMaxWidthPx={HUB_TOOLTIP_WIDTH_PX}
                    />
                  </div>
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </StyledSatelliteInFovListItem>
  );
}

export default SatelliteInFovListItem;

interface StyledSatelliteInFovListItemProps extends StyledProps {
  hasHcxDistanceToEdge: number;
  hideStats: boolean;
}

const StyledSatelliteInFovListItem = styled.div`
  height: ${({ hasHcxDistanceToEdge, hideStats }: StyledSatelliteInFovListItemProps) =>
    hasHcxDistanceToEdge ? 125 : hideStats ? 30 : 100}px;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  .faded {
    opacity: 0.6;
  }
  .data-wrapper {
    left: 20px;
    width: 88.88%;
    height: 100%;
    position: absolute;
  }
  .circle {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #000000
    text-align: center;
    margin-right: 5px;
    font-size: 15px;
    font-weight: bold;
  }
  .background__line {
    width: 100%;
    height: 1px;
    background-color: #465967;
  }
  .header {
    position: relative;
    font-size: 14px;
    height: ${({ hasHcxDistanceToEdge }: StyledSatelliteInFovListItemProps) => (hasHcxDistanceToEdge ? 28 : 32)}px;
    align-items: ${({ hasHcxDistanceToEdge }: StyledSatelliteInFovListItemProps) =>
      hasHcxDistanceToEdge ? 'end' : 'center'};
    line-height: 20px;
    display: flex;
  }
  .satellite {
    padding-left: 5px;
    position: relative;
  }
  .connectivity {
    position: absolute;
    right: 0px;
    top: ${({ hasHcxDistanceToEdge }: StyledSatelliteInFovListItemProps) => (hasHcxDistanceToEdge ? 8 : 7)}px;
    text-transform: capitalize;
    font-weight: 600;
  }
  .stat-wrapper {
    position: relative;
    height: 68px;
  }
  .label {
    top: 15px;
    position: absolute;
    font-size: 14px;
    height: 20px;
    letter-spacing: 0px;
    line-height: 20px;

    color: ${({ theme }: StyledSatelliteInFovListItemProps) => theme.primaryTextColor};
  }
  .value {
    top: 38px;
    font-size: 14px;
    font-weight: 600;
    height: 20px;
    letter-spacing: 0px;
    line-height: 20px;
    position: absolute;
  }
  .azimuth {
    left: 0px;
  }
  .elevation {
    left: 80px;
  }
  .hub {
    left: 169px;
  }
  .link {
    left: 291px;
  }
`;
